import { FirebaseApp, initializeApp } from "@firebase/app";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, GoogleAuthProvider, linkWithPopup, signInWithPopup, User, Auth, updatePassword, sendPasswordResetEmail } from "@firebase/auth";
import { ISubtitles, IVideoResponse } from "../types/videoResponse";
import { GenderType, IClientData, IClientFormData, ISession } from "../types/dashboard";
import { sendFileChunked } from "./uploadAvatar";
import { Environment } from '../utils/environment';
import { setConnectionError } from '../utils/connectionState';
import { getApiUrl } from '../utils/apiConfig';

const url = getApiUrl();

const initialized: {
    auth?: Auth,
    app?: FirebaseApp
} = {};

interface IServerUser{
    Name: string,
    Soname: string,
    gender: GenderType,
    email: string,
    dateOfBirth: string,
    notes: string,
    scheduleDay: number,
    customAvatarUrl?: string,
    Datetime?: number  // UTC timestamp for dateOfBirth (backend compatibility)
}

const clientToServer = (data: IClientData | IClientFormData)=>{
    const result: IServerUser = {
        Name: data.firstName, 
        Soname: data.lastName,
        gender: data.gender,
        email: data.email,
        dateOfBirth: data.dateOfBirth,
        notes: data.notes,
        scheduleDay: data.scheduleDay,
        customAvatarUrl: data.customAvatarUrl
    }
    
    // Add Datetime field for dateOfBirth if present (UTC timestamp for backend)
    if (data.dateOfBirth) {
        try {
            const birthDate = new Date(data.dateOfBirth);
            if (!isNaN(birthDate.getTime())) {
                result.Datetime = Math.floor(birthDate.valueOf() / 1000);
            }
        } catch (e) {
            console.warn('Failed to convert dateOfBirth to UTC timestamp:', e);
        }
    }
    
    return result;
}

export const handleDisabledAccount = () => {
    signOut(initialized.auth);
    // Auth component will handle redirect to login page
};

// Helper function to safely get the auth token
async function getAuthToken(): Promise<string | null> {
    if (!initialized.auth || !initialized.auth.currentUser) {
        return null; // Or throw an error, or redirect to login
    }
    try {
        return await initialized.auth.currentUser.getIdToken();
    } catch (error) {
        console.error("Error getting auth token:", error);
        // Handle token refresh or re-authentication if needed
        return null;
    }
}

export function initApi(){
    // Конфигурация Firebase
    const firebaseConfig = {
        apiKey: "AIzaSyCIvuFax2pUCnvpBPdoAkq9DsUHcdDqubM",
        authDomain: "nikitasolaris-e1ffa.firebaseapp.com",
        projectId: "nikitasolaris-e1ffa",
        storageBucket: "nikitasolaris-e1ffa.appspot.com",
        messagingSenderId: "263989569139",
        appId: "1:263989569139:web:a59c374cc00d8aea2b6c06",
        measurementId: "G-FPN3SDRCVT"
    };

    // Инициализация Firebase
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    console.log(auth);

    //auth.currentUser
    //console.log(auth.currentUser.email);

    initialized.app = app;
    initialized.auth = auth;
}

export const changePassword = async (oldPassword: string, newPassword: string) => {
    const secureAuth = await signInUser(initialized.auth.currentUser.email, oldPassword);
    return updatePassword(secureAuth.user, newPassword);
}
export const onAuth = (handler: (user: User | null) => void) => onAuthStateChanged(initialized.auth, handler);
export const registerUser = (email: string, password: string) => createUserWithEmailAndPassword(initialized.auth, email, password);
export const signInUser = (email: string, password: string) => signInWithEmailAndPassword(initialized.auth, email, password);
export const signOutUser = () => signOut(initialized.auth);
export const signInWithGoogle = () => {    
    const provider = new GoogleAuthProvider();
    //provider.addScope('https://www.googleapis.com/auth/drive');
    return signInWithPopup(initialized.auth, provider).then(result=>{
        const credential = GoogleAuthProvider.credentialFromResult(result);
        console.log(credential);
        //localStorage.setItem('gtoken', credential.accessToken);
        return result;
    });
}

export const linkGoogleWithPopup = ()=>{
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/drive');
    return linkWithPopup(initialized.auth.currentUser, provider).then(result=>{
        const credential = GoogleAuthProvider.credentialFromResult(result);
        console.log(credential);
        localStorage.setItem('gtoken', credential.accessToken);
        return credential;
    }).catch(err=>{
        const credential = GoogleAuthProvider.credentialFromError(err);
        console.log(credential);
        localStorage.setItem('gtoken', credential.accessToken);
        return credential;
    })
}

export const resetPassword = (email: string)=>{
    return sendPasswordResetEmail(initialized.auth, email);
}

export const getClients = async() => {
    const token = await getAuthToken();
    return fetch(url + 'client', {
        method: "GET",
        headers: {
            'Authorization': `Why_they_write_a_Bearer_here ${token}`
        }
    });
};

export const postClient = async(data: IClientFormData) => {
    const token = await getAuthToken();
    return fetch(url + 'client', {
        method: "POST",
        body: JSON.stringify(clientToServer(data)),
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        }
    });
};

export const putClient = async(clientId: string, data: IClientFormData) => {
    const token = await getAuthToken();
    return fetch(url + 'client/' + clientId, {
        method: "PUT",
        body: JSON.stringify(clientToServer(data)),
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        }
    });
};

export const deleteClient = async(clientId: string) => {
    const token = await getAuthToken();
    return fetch(url + 'client/' + clientId, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        }
    });
};

export const sendRequest = async(path: string, method: string, data: any, clientId: string) => {
    const token = await getAuthToken();
    try {
        const response = await fetch(url + path + (clientId || ''), {
            method: method,
            body: data && JSON.stringify(data),
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`
            }
        });

        setConnectionError(false);

        if (response.status === 401) {
            const clonedResponse = response.clone();
            const data = await clonedResponse.json();
            if (data.Error === "Account disabled") {
                handleDisabledAccount();
                return null;
            }
        }
        return response;
    } catch (error) {
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
            setConnectionError(true);
            return null;
        }
        throw error;
    }
};

type IDeleteAction = 'delete-single' | 'delete-all' | 'delete-after' | 'delete-before' | 'delete-amount-before' | 'delete-amount-after';

export const uploadJson = async(data: {session_id: string, output: ISubtitles})=>{
    //{ session_id: sessionId, output: output }
    return sendRequest('uploadJson', 'POST', data, '');
}

export const deleteCommonWord = async(sessionId: string, wordId: number) => {
    const path = `session/${sessionId}/deleteCommonWord/${wordId}`;
    return sendRequest(path, 'POST', {}, '');
}

export const uploadAvatar = async (file: Blob, onProgress: (current: number, total: number)=>void) => {
    const token = await getAuthToken();
    const result = sendFileChunked(file, 'avatar'+Date.now(), token, url + 'uploadAvatar', CHUNK_SIZE, onProgress);
    return result;
}

const CHUNK_SIZE = 1024 * 1024;

export const uploadMindmapMedia = async (file: Blob, fileName: string, onProgress: (current: number, total: number)=>void) => {
    const token = await getAuthToken();
    const result = await sendFileChunked(file, fileName, token, url + 'mindmap/uploadMedia', CHUNK_SIZE, onProgress);
    return result;
}

export const sendFiles = async(files: FileList, sessionId: string, onProgress: (current: number, total: number)=>void) => {
  //  event.preventDefault();  // Предотвращаем стандартное поведение формы

   // const sessionId = uploadSessionIdInput.value; // Получаем Session ID
   // const file = fileInput.files[0];
   const file = files[0];

    /*if (!sessionId) {
        uploadResponseDiv.innerText = "Пожалуйста, введите Session ID.";
        return;
    }

    if (!file) {
        uploadResponseDiv.innerText = "Пожалуйста, выберите файл.";
        return;
    }*/

    const fileName = file.name;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let offset = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        await uploadChunk(chunk, chunkIndex, totalChunks, fileName, sessionId);
        onProgress(chunkIndex, totalChunks);
        offset += CHUNK_SIZE;
    }
};

// Функция загрузки чанка
async function uploadChunk(chunk: Blob, chunkIndex: number, totalChunks: number, fileName: string, sessionId: string) {
    const formData = new FormData();
    formData.append('file', chunk, `chunk-${chunkIndex}`);
    formData.append('chunkIndex', chunkIndex.toString());
    formData.append('totalChunks', totalChunks.toString());
    formData.append('fileName', fileName);
    formData.append('session_id', sessionId); // Добавляем Session ID

    const token = await getAuthToken();
    /*if (!token) {
        uploadResponseDiv.innerText = "Ошибка аутентификации. Пожалуйста, войдите.";
        return;
    }

    // Обновление прогресса
    uploadResponseDiv.innerText = `Загрузка чанка ${chunkIndex + 1} из ${totalChunks}...`;
*/
    // Отправка чанка на сервер
    try {
        const response = await fetch(url + 'upload', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        // Проверяем, если ответ не OK
        if (!response.ok) {
            const errorText = await response.text(); // Получаем текст ошибки
            throw new Error(`Ошибка: ${response.status} - ${errorText}`); // Исключение с сообщением об ошибке
        }

        const result = await response.json();
        if (result.url) {
            //window.location.href = result.url; // Переход на страницу результата
        } else {
            //uploadResponseDiv.innerText = "Файл загружен успешно."; // Успешное сообщение
        }
    } catch (error) {
        //uploadResponseDiv.innerText = "Ошибка: " + error.message; // Выводим сообщение об ошибке
        console.error(error); // Выводим ошибку в консоль для отладки
        throw error;
    }
}

export const loadFile = async (sessionId: string)=>{
    const path = 'result/'+sessionId;
    const method = 'GET';
    const token = await getAuthToken();

    // Add timeout and better error handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
        const response = await fetch(url + path, {
            method: method,
            signal: controller.signal,
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${token}`,
            }
        });

        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
}

/*export const postFile = async(data: FormData)=>{
    const path = 'upload';
    const method = 'POST';
    const token = await initialized.auth.currentUser.getIdToken();
    return fetch(url + path, {
        method: method,
        body: data,
        headers: {
           /* "Content-Type": "multipart/form-data",*/
/*            'Authorization': `Bearer ${token}`
        }
    });
}*/

// session
// calendar

export const getUserData = async () => {
    const token = await getAuthToken();
    return fetch(url + 'user', {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`
        }
    }).then(response => response.json());
};


export const getDailyStats = async() => {
    const token = await getAuthToken();
    return fetch(url + 'getDailyStats', {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`
        }
    }).then(response => response.json());
};

export const disableUser = async(userId: string, disabled: boolean) => {
    const token = await getAuthToken();
    return fetch(url + 'disableUser', {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId, disabled })
    });
};

export const createUser = async(email: string, password: string, firstName: string, lastName: string) => {
    const token = await getAuthToken();
    return fetch(url + 'createUser', {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email, password, firstName, lastName })
    });
};

export const getLifeline = async(clientId: string) => {
    const token = await getAuthToken();
    return fetch(url + 'lifeline/' + clientId, {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
};

export const updateLifelineEvent = async(clientId: string, eventId: string, eventData: any) => {
    const token = await getAuthToken();
    return fetch(url + 'lifeline/' + clientId + '/' + eventId, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
    });
};

export const deleteLifelineEvent = async(clientId: string, eventId: string) => {
    const token = await getAuthToken();
    return fetch(url + 'lifeline/' + clientId + '/' + eventId, {
        method: "DELETE",
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
};

export const createLifelineEvent = async(clientId: string, eventData: any) => {
    const token = await getAuthToken();
    return fetch(url + 'lifeline/' + clientId, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
    });
};

export const getLatestSessions = async() => {
    const token = await getAuthToken();
    return fetch(url + 'getLatestSessions', {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
};

export const loadMultiSessionData = async (clientId: string) => {
    const path = `session/${clientId}?result=true`;
    const method = 'GET';
    const token = await getAuthToken();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
        const response = await fetch(url + path, {
            method: method,
            signal: controller.signal,
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${token}`,
            }
        });

        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
};

export const sendReport = async(message: string, logs?: any, screenshot?: any) => {
    const token = await getAuthToken();
    const user_id = initialized.auth.currentUser.uid;

    return fetch(url + 'report', {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
            user_id, 
            message, 
            logs: logs || null, 
            screenshot: screenshot || null 
        })
    });
};

export const deleteReport = async(reportId: string) => {
    const token = await getAuthToken();
    return fetch(url + 'deleteReport', {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ report_id: reportId })
    });
};